/**
 * filterTransform — single source of truth for FiltersState → API request body.
 *
 * All camelCase→snake_case mapping lives here. Adding a filter field means
 * editing this file and its test; nothing else needs to change.
 */

import type { FiltersState } from '../types/stores';
import type { MapFilters } from './searchCriteria';

export interface SearchRequestBody {
  city: string;
  neighborhoods?: string[];
  bedrooms?: string[];
  max_budget?: number;
  min_budget?: number;
  budget_flexibility?: string;
  walkability_tiers?: string[];
  lifestyle_profiles?: string[];
  vibe_words?: string[];
  standard_amenities?: string[];
  building_ages?: string[];
  min_rating?: number;
  pet_friendly?: boolean;
  min_year_built?: number;
  min_sqft?: number;
  has_special?: boolean;
  senior_housing?: boolean;
  student_housing?: boolean;
  no_ground_floor?: boolean;
  must_have_covered_parking?: boolean;
  must_allow_large_dogs?: boolean;
  move_in_timeline?: string;
  natural_language?: string;
  why_austin?: string[];
  lease_term?: string;
  work_location?: string;
  work_location_lat?: number;
  work_location_lng?: number;
  max_commute_minutes?: number;
  user_profile?: Record<string, unknown>;
  pinned_apartment_names?: string[];
  ai_curated?: boolean;
  // Skip server-side LLM re-rank + explanation enrichment (browse map).
  lean?: boolean;
  limit?: number;
  offset?: number;
  // Neighbourhood-load-more extras
  exclude_names?: string[];
  // Map search extras
  south_lat?: number;
  north_lat?: number;
  west_lng?: number;
  east_lng?: number;
}

export interface FilterPreviewRequestBody {
  city: string;
  neighborhoods?: string[];
  bedrooms?: string[];
  walkability_tiers?: string[];
  lifestyle_profiles?: string[];
  vibe_words?: string[];
  standard_amenities?: string[];
  building_ages?: string[];
  max_budget?: number;
  min_budget?: number;
  budget_flexibility?: string;
  min_rating?: number;
  has_special?: boolean;
  senior_housing?: boolean;
  student_housing?: boolean;
  pet_friendly?: boolean;
  min_year_built?: number;
  min_sqft?: number;
}

const BUDGET_FLEX_MAP: Record<string, string> = {
  strict: 'strict',
  some: 'some_wiggle',
  lots: 'flexible',
};

function arr<T>(v: T[] | undefined | null): T[] | undefined {
  return v?.length ? v : undefined;
}

function pos(v: number | null | undefined): number | undefined {
  return v || undefined;
}

function flag(v: boolean | undefined): boolean | undefined {
  return v || undefined;
}

export function toSearchRequest(
  filters: FiltersState,
  city: string,
  extras?: {
    userProfile?: Record<string, unknown>;
    pinnedApartmentNames?: string[];
    limit?: number;
    offset?: number;
    excludeNames?: string[];
    bounds?: { south_lat: number; north_lat: number; west_lng: number; east_lng: number };
  }
): SearchRequestBody {
  return {
    city,
    neighborhoods: arr(filters.neighborhoods),
    bedrooms: arr(filters.bedrooms),
    max_budget: pos(filters.maxBudget),
    min_budget: pos(filters.minBudget),
    budget_flexibility: filters.budgetFlex ? (BUDGET_FLEX_MAP[filters.budgetFlex] ?? filters.budgetFlex) : undefined,
    walkability_tiers: arr(filters.walkabilityTiers),
    lifestyle_profiles: arr(filters.lifestyleProfiles),
    vibe_words: arr(filters.vibeWords),
    standard_amenities: arr(filters.standardAmenities),
    building_ages: arr(filters.buildingAges),
    min_rating: pos(filters.minRating),
    pet_friendly: flag(filters.petFriendly),
    min_year_built: pos(filters.minYear),
    min_sqft: pos(filters.minSqft),
    has_special: flag(filters.hasSpecial),
    senior_housing: flag(filters.seniorHousing),
    student_housing: flag(filters.studentHousing),
    no_ground_floor: flag(filters.noGroundFloor),
    must_have_covered_parking: flag(filters.mustHaveCoveredParking),
    must_allow_large_dogs: flag(filters.mustAllowLargeDogs),
    move_in_timeline: filters.moveInTimeline ?? undefined,
    natural_language: filters.naturalLanguage || undefined,
    why_austin: arr(filters.whyAustinList),
    lease_term: filters.leaseTerm ?? undefined,
    work_location: filters.workLocationText || undefined,
    work_location_lat: filters.workLocationCoords?.lat || undefined,
    work_location_lng: filters.workLocationCoords?.lng || undefined,
    max_commute_minutes: pos(filters.maxCommute),
    user_profile: extras?.userProfile,
    pinned_apartment_names: arr(extras?.pinnedApartmentNames),
    ai_curated: filters.aiCurated || false,
    limit: extras?.limit,
    offset: extras?.offset,
    exclude_names: arr(extras?.excludeNames),
    ...extras?.bounds,
  };
}

export function toFilterPreviewRequest(
  filters: FiltersState,
  city: string
): FilterPreviewRequestBody {
  return {
    city,
    neighborhoods: filters.neighborhoods,
    bedrooms: filters.bedrooms,
    walkability_tiers: filters.walkabilityTiers,
    lifestyle_profiles: filters.lifestyleProfiles,
    vibe_words: filters.vibeWords,
    standard_amenities: filters.standardAmenities,
    building_ages: filters.buildingAges,
    max_budget: pos(filters.maxBudget),
    min_budget: pos(filters.minBudget),
    // Same vocabulary mapping as toSearchRequest — the preview count must be
    // computed under the same budget semantics as the search that follows.
    budget_flexibility: filters.budgetFlex ? (BUDGET_FLEX_MAP[filters.budgetFlex] ?? filters.budgetFlex) : undefined,
    min_rating: filters.minRating ?? undefined,
    has_special: filters.hasSpecial,
    senior_housing: filters.seniorHousing,
    student_housing: filters.studentHousing,
    pet_friendly: filters.petFriendly,
    min_year_built: pos(filters.minYear),
    min_sqft: pos(filters.minSqft),
  };
}

// ── ExplainContext ────────────────────────────────────────────────────────────
// Lives here (not in searchApi.ts) so it can be tested without the HTTP layer.

export interface ExplainContext {
  max_budget?: number | null;
  bedrooms?: string[];
  neighborhoods?: string[];
  natural_language?: string;
  lifestyle_profiles?: string[];
  vibe_words?: string[];
  standard_amenities?: string[];
  deal_breakers?: string[];
  pet_friendly?: boolean;
  move_in_timeline?: string | null;
}

export function explainContextFromFilters(filters: {
  maxBudget?: number | null;
  bedrooms?: string[];
  neighborhoods?: string[];
  naturalLanguage?: string;
  lifestyleProfiles?: string[];
  vibeWords?: string[];
  standardAmenities?: string[];
  petFriendly?: boolean;
  moveInTimeline?: string | null;
}): ExplainContext {
  return {
    max_budget:         filters.maxBudget,
    bedrooms:           filters.bedrooms,
    neighborhoods:      filters.neighborhoods,
    natural_language:   filters.naturalLanguage,
    lifestyle_profiles: filters.lifestyleProfiles,
    vibe_words:         filters.vibeWords,
    standard_amenities: filters.standardAmenities,
    pet_friendly:       filters.petFriendly,
    move_in_timeline:   filters.moveInTimeline,
  };
}

// Map browse queries the server with the SHARED MapFilters state (the same
// state the vibe map narrows with, seeded from the Criteria panel per
// ADR-0001) — one filter vocabulary across both map surfaces.
export function toBrowseSearchRequest(
  mapFilters: MapFilters,
  city: string,
  bounds: { south_lat: number; north_lat: number; west_lng: number; east_lng: number },
  extras?: { limit?: number; offset?: number }
): SearchRequestBody {
  return {
    city,
    bedrooms: arr(mapFilters.bedrooms),
    max_budget: pos(mapFilters.maxBudget),
    min_budget: pos(mapFilters.minBudget),
    budget_flexibility: undefined,
    building_ages: arr(mapFilters.buildingAges),
    ...bounds,
    limit: extras?.limit ?? 50,
    offset: extras?.offset ?? 0,
    // The map consumes neither LLM re-ranking nor explanations; lean skips
    // both server-side (that work OOM'd the free-tier backend).
    lean: true,
  };
}

// Build the snake_case `current_filters` payload sent to /api/vibe-search/chat
// (the vibe dialect: budget_max/budget_min, vibes, dealbreakers-as-strings —
// distinct from the /search dialect above, but defined in the SAME file so
// the two projections of one FiltersState can't drift apart unnoticed).
// Mirrors the subset of apartmentStore.filters the backend reads for its
// preferences block (_inject_user_context + [USER_CONTEXT] blob). Boolean
// dealbreaker flags collapse to a string list. Returns undefined when empty
// so the field is omitted from the request body.
export function toVibeContext(
  filters: FiltersState,
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  if (filters.bedrooms?.length) out.bedrooms = filters.bedrooms;
  if (filters.maxBudget) out.budget_max = filters.maxBudget;
  if (filters.minBudget) out.budget_min = filters.minBudget;
  if (filters.neighborhoods?.length) out.neighborhoods = filters.neighborhoods;
  if (filters.petFriendly) out.pet_friendly = true;
  if (filters.vibeWords?.length) out.vibes = filters.vibeWords;
  if (filters.standardAmenities?.length) out.amenities = filters.standardAmenities;
  if (filters.workLocationText) out.work_location = filters.workLocationText;
  if (filters.maxCommute) out.max_commute = filters.maxCommute;
  if (filters.minSqft) out.min_sqft = filters.minSqft;

  // Dealbreakers from boolean flags (mirrors userProfileStore.updateFromCriteria)
  const dbs: string[] = [];
  if (filters.noGroundFloor) dbs.push('no ground floor');
  if (filters.noStreetFacing) dbs.push('no street facing');
  if (filters.noCarpet) dbs.push('no carpet');
  if (filters.mustHaveCoveredParking) dbs.push('must have covered parking');
  if (filters.mustAllowLargeDogs) dbs.push('must allow large dogs');
  if (dbs.length > 0) out.dealbreakers = dbs;

  return Object.keys(out).length > 0 ? out : undefined;
}
